import { AmenityPOI } from '@popety-io/types'

/**
 * Facilities such as sports pitches, playgrounds and recycling points have no
 * name in OpenStreetMap because they are not named entities — around half of
 * the Wellness and Basic Amenities POIs near a Swiss address are in this state.
 * Listing them individually produced a wall of identical "Unknown" rows, so the
 * card groups them by type instead.
 */

/** Minimum members before a set of unnamed facilities is worth collapsing. */
const MIN_GROUP_SIZE = 3

/**
 * Historical amenity blobs were persisted with the literal string `Unknown`
 * where a name was missing, so treat it as absent alongside empty/undefined.
 */
export const isUnnamed = (poi: AmenityPOI): boolean =>
  !poi.name || poi.name.trim() === '' || poi.name === 'Unknown'

export type AmenityRow =
  | { kind: 'poi'; poi: AmenityPOI; distance: number }
  | {
      kind: 'group'
      type: string
      /** Subtype this group belongs to, used to look up its true radius count. */
      subCategory: string
      count: number
      distance: number
    }

/**
 * Turns a distance-sorted POI list into display rows, collapsing runs of
 * unnamed facilities of the same type into a single counted row placed at the
 * distance of its nearest member — so a group never jumps the queue or sinks
 * below places that are further away.
 */
export const groupUnnamedAmenities = (
  items: AmenityPOI[],
  /**
   * True unnamed counts per subtype across the whole radius, from the ES
   * aggregation. Without it the group would report only the unnamed items that
   * happened to be among the nearest fetched — the same under-reporting the
   * aggregation exists to prevent.
   */
  unnamedBySubCategory?: Map<string, number>
): AmenityRow[] => {
  const groups = new Map<
    string,
    { count: number; distance: number; subCategory: string }
  >()

  for (const poi of items) {
    if (!isUnnamed(poi)) continue
    const existing = groups.get(poi.type)
    if (existing) {
      existing.count += 1
      existing.distance = Math.min(existing.distance, poi.distance)
    } else {
      groups.set(poi.type, {
        count: 1,
        distance: poi.distance,
        subCategory: poi.sub_category,
      })
    }
  }

  const collapsed = new Set(
    [...groups.entries()]
      .filter(([, g]) => g.count >= MIN_GROUP_SIZE)
      .map(([type]) => type)
  )

  const rows: AmenityRow[] = []
  const emitted = new Set<string>()

  for (const poi of items) {
    if (isUnnamed(poi) && collapsed.has(poi.type)) {
      if (emitted.has(poi.type)) continue
      emitted.add(poi.type)
      const group = groups.get(poi.type)!
      const trueCount = unnamedBySubCategory?.get(group.subCategory)
      rows.push({
        kind: 'group',
        type: poi.type,
        subCategory: group.subCategory,
        // Prefer the radius-wide count; fall back to what we can see.
        count:
          typeof trueCount === 'number' && trueCount > group.count
            ? trueCount
            : group.count,
        distance: group.distance,
      })
      continue
    }
    rows.push({ kind: 'poi', poi, distance: poi.distance })
  }

  return rows
}
